import {Translatable} from 'common/i18n';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import type {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {PersistedBoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {Segmentation} from 'common/types/reports/query';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {ImpactReportAttrs} from 'apps/impact/src/models/report';
import {JqlBookmarkParams} from 'apps/jql-console/src/jql-console/types';

import type {DashboardOrigin} from '../util/bookmark-overrides';
import {FeatureRolloutName} from '../util/feature-sampling';
import {FlowsBookmark} from '../flows/models/types';
import {FunnelBookmark} from '../funnels/models/types';

// These values must match template types in webapp/dashboard/models.py
export enum TemplateType {
  KPI = `KPI`,
  FEATURE_LAUNCH = `Feature Launch`,
  LIFECYCLE_COHORT_ANALYSIS = `Lifecycle Cohort Analysis`,
  MARKETING_KPI = `Marketing KPI`,
  E_COMMERCE = `E-Commerce`,
  WEB_ANALYTICS = `Web Analytics`,
  STARTER_BOARD = `Starter Board`,
  BENCHMARK_KPI = `Benchmark KPI`,
  B2B = `B2B`,
  SAAS_COMPANY_KPI = `SAAS Company KPIs`,
}

// These values must match benchmark card types in webapp/dashboard/models.py
export enum BenchmarkCardType {
  MOM_GROWTH = `mom_growth`,
  AVG_SESSION = `avg_session`,
  ONE_WEEK_RETENTION = `one_week_retention`,
  USER_STICKINESS = `user_stickiness`,
}

export type BookmarkParamsUnionType =
  | InsightsBookmarkParams
  | FunnelBookmark
  | FlowsBookmark
  | ImpactReportAttrs
  | JqlBookmarkParams;

export type TemplateField =
  | KPITemplateFields
  | ProductLaunchTemplateFields
  | LifecycleCohortAnalysisTemplateFields
  | MarketingKPITemplateFields
  | ECommerceTemplateFields
  | WebAnalyticsTemplateFields
  | B2BTemplateFields
  | SaaSCompanyKpiTemplateFields;

export enum KPITemplateFields {
  SIGN_UP = `Sign Up`,
  VALUE_MOMENT = `Value Moment`,
}

export enum ProductLaunchTemplateFields {
  FEATURE_NAME = `Feature Name`,
  LAUNCH_DATE = `Launch Date`,
  NEW_FEATURE = `New Feature`,
  FEATURE_VALUE_MOMENT = `Feature Value Moment`,
}

export enum LifecycleCohortAnalysisTemplateFields {
  VALUE_MOMENT = `Value Moment`,
}

export enum MarketingKPITemplateFields {
  ACTIVATION_EVENT = `Activation Event`,
  CONVERSION_EVENT = `Conversion Event`,
  AD_SPEND_EVENT = `Ad Spend Event`,
  AD_COST_PROPERTY = `Ad Cost`,
  AD_CLICKS_PROPERTY = `Ad Clicks`,
  AD_IMPRESSIONS_PROPERTY = `Ad Impressions`,
  CONVERSION_PURCHASE_AMOUNT_PROPERTY = `Conversion Purchase Amount`,
}

export enum ECommerceTemplateFields {
  PURCHASE_EVENT = `Purchase`,
  ADD_TO_CART_EVENT = `Add to Cart`,
  PRODUCT_VIEWED_EVENT = `Product Viewed`,
  CART_PROPERTY = `Cart Property`,
  CART_PROPERTY_2 = `Cart Property 2`,
  PRICE_KEY = `Price Key`,
  CATEGORY_KEY = `Category Key`,
}

export enum WebAnalyticsTemplateFields {
  PAGE_LOADED_EVENT = `Page Loaded`,
  PAGE_NAME_PROPERTY = `Page Name`,
}

export enum B2BTemplateFields {
  DATA_GROUP_ID = `Data Group`,
  ACTIVATION_EVENT = `Activation Event`,
  ACCOUNT_NAME_PROPERTY_FIELD = `Account Name`,
  ARR_PROPERTY_FIELD = `Account ARR`,
  SUBSCRIPTION_PLAN_TYPE_PROPERTY_FIELD = `Subscription Plan Type`,
}

export enum SaaSCompanyKpiTemplateFields {
  DATA_GROUP_ID = `Data Group`,
  MRR_PROPERTY_FIELD = `MRR`,
  PLAN_TYPE_PROPERTY_FIELD = `Subscription Plan Type`,
  SEGMENT_PROPERTY_FIELD = `Segment`,
}

const WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;

export type TemplateSpecsKeys =
  | TemplateType.KPI
  | TemplateType.FEATURE_LAUNCH
  | TemplateType.LIFECYCLE_COHORT_ANALYSIS
  | TemplateType.MARKETING_KPI
  | TemplateType.E_COMMERCE
  | TemplateType.WEB_ANALYTICS
  | TemplateType.B2B
  | TemplateType.SAAS_COMPANY_KPI;

export type TemplateSpecsEntry = {
  title: string;
  type: TemplateType;
  templateSelectDescription: string;
  imageId: string;
  eventSelectStepDescription: string;
  demoBoardLink: string;
  moreInfo?: {text: string; link: string};
  requiredFields: TemplateField[];
  keywords: string[];
  rhsDescription?: string;
  requiredFieldsCheck?: () => boolean;
  /**
   * Optionally include a feature flag that's required to display the template.
   */
  featureRolloutName?: FeatureRolloutName;
  titleBadge?: string;
};

// TODO (@rhoeuns) combine with TemplateSpecs as one const after localization has applied to search and RHS components
export const TemplateSpecsTranslationKey: Record<TemplateSpecsKeys, {title: Translatable; description: Translatable}> =
  {
    [TemplateType.KPI]: {
      title: ({t}) => t(`boards.template-modal.kpi.title`),
      description: ({t}) => t(`boards.template-modal.kpi.description`),
    },
    [TemplateType.FEATURE_LAUNCH]: {
      title: ({t}) => t(`boards.template-modal.feature-launch.title`),
      description: ({t}) => t(`boards.template-modal.feature-launch.description`),
    },
    [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: {
      title: ({t}) => t(`boards.template-modal.lifecycle.title`),
      description: ({t}) => t(`boards.template-modal.lifecycle.description`),
    },
    [TemplateType.E_COMMERCE]: {
      title: ({t}) => t(`boards.template-modal.ecommerce.title`),
      description: ({t}) => t(`boards.template-modal.ecommerce.description`),
    },
    [TemplateType.MARKETING_KPI]: {
      title: ({t}) => t(`boards.template-modal.marketing.title`),
      description: ({t}) => t(`boards.template-modal.marketing.description`),
    },
    [TemplateType.WEB_ANALYTICS]: {
      title: ({t}) => t(`boards.template-modal.web-analytics.title`),
      description: ({t}) => t(`boards.template-modal.web-analytics.description`),
    },
    [TemplateType.B2B]: {
      title: ({t}) => t(`boards.template-modal.b2b.title`),
      description: ({t}) => t(`boards.template-modal.b2b.description`),
    },
    [TemplateType.SAAS_COMPANY_KPI]: {
      title: ({t}) => t(`boards.template-modal.saas-company-kpi.title`),
      description: ({t}) => t(`boards.template-modal.saas-company-kpi.description`),
    },
  };

export const TemplateSpecs: Record<TemplateSpecsKeys, TemplateSpecsEntry> = {
  [TemplateType.KPI]: {
    title: `Company KPIs`,
    type: TemplateType.KPI,
    templateSelectDescription: `Nine reports based on core metrics applicable to most products: engagement, retention, and growth.`,
    imageId: `kpi`,
    eventSelectStepDescription: `To use the Company KPI Template, select your corresponding event for each of the suggested events shown.`,
    rhsDescription: `This template is all about your core company metrics and requires two events to get started.`,
    demoBoardLink: `https://mixpanel.com/p/QLBHa24vdYuK2MJLiNUA1S`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [KPITemplateFields.SIGN_UP, KPITemplateFields.VALUE_MOMENT],
    keywords: [
      `Company`,
      `KPI`,
      `KPIs`,
      `Key Performance Indicators`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `Core Performance Indicators`,
      `Core Metrics`,
      `DAU`,
      `MAU`,
      `WAU`,
      `Starter Template`,
      `Template`,
    ],
  },
  [TemplateType.MARKETING_KPI]: {
    title: `Marketing KPIs`,
    type: TemplateType.MARKETING_KPI,
    templateSelectDescription: `Fifteen reports to help you optimize your marketing strategy.`,
    imageId: `marketing-kpi`,
    eventSelectStepDescription: `To use the Marketing KPI Template, enter up to two events: what makes an active user and the action you want users to take.`,
    rhsDescription: `This template is made up of the core metrics to help you optimize your marketing strategy.`,
    demoBoardLink: `https://mixpanel.com/p/MKDgQSoYBZciN4AGgY7Mgh`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [MarketingKPITemplateFields.CONVERSION_EVENT],
    keywords: [
      `Marketing`,
      `KPIs`,
      `Key Performance Indicators`,
      `User base`,
      `Marketing Campaign`,
      `Activation`,
      `Conversion`,
      `Acquisition`,
      `Session engagement`,
      `Conversion journey`,
      `Template`,
    ],
  },
  [TemplateType.WEB_ANALYTICS]: {
    title: `Web Analytics`,
    type: TemplateType.WEB_ANALYTICS,
    templateSelectDescription: `Sixteen reports to evaluate core user, session and page metrics.`,
    eventSelectStepDescription: `To use the Marketing KPI Template, we recommend starting with two key events: the action that defines an active user, and the action you want users to take. Optionally, you can also include ad data to expand this template.`,
    imageId: `web-analytics`,
    demoBoardLink: `https://mixpanel.com/project/3018488/view/3536632/app/boards#id=6020908`,
    requiredFields: [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT, WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY],
    keywords: [`Web Analytics`, `Website Metrics`, `Template`],
  },
  [TemplateType.FEATURE_LAUNCH]: {
    title: `Feature Launch`,
    type: TemplateType.FEATURE_LAUNCH,
    templateSelectDescription: `Fourteen reports to help you evaluate adoption, retention and overall product impact of your new feature.`,
    imageId: `feature-launch`,
    eventSelectStepDescription: `To use the Feature Launch Template, fill out details about your feature launch and select your corresponding event for each of the suggested events shown.`,
    rhsDescription: `This template is made up of the most commonly used and actionable product metrics for tracking launches.`,
    demoBoardLink: `https://mixpanel.com/p/5dbqyToAtFMaDJVJNzUeuG`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [
      ProductLaunchTemplateFields.FEATURE_NAME,
      ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
      ProductLaunchTemplateFields.LAUNCH_DATE,
      ProductLaunchTemplateFields.NEW_FEATURE,
    ],
    keywords: [
      `Feature`,
      `Launch`,
      `Product Launch`,
      `Feature Release`,
      `Product release`,
      `New Feature`,
      `Launching Feature`,
      `Starter Template`,
      `Template`,
      `Feature adoption`,
      `Activation rate`,
      `Template`,
    ],
  },
  [TemplateType.E_COMMERCE]: {
    title: `Ecommerce`,
    type: TemplateType.E_COMMERCE,
    templateSelectDescription: `Eighteen reports to provide common strategic plans and goals of an ecommerce business.`,
    eventSelectStepDescription: `The Ecommerce template requires object properties underneath two events. If you don't have an object property to represent your shopping cart, learn how to set it up`,
    rhsDescription: `To use this template we require three events and four properties.`,
    imageId: `e-commerce`,
    demoBoardLink: ` https://mixpanel.com/project/3018488/view/3536632/app/boards#id=8229971`,
    moreInfo: {
      text: `here.`,
      link: `https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#list-of-objects-property-support`,
    },
    requiredFields: [
      ECommerceTemplateFields.ADD_TO_CART_EVENT,
      ECommerceTemplateFields.CART_PROPERTY,
      ECommerceTemplateFields.CATEGORY_KEY,
      ECommerceTemplateFields.PRICE_KEY,
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      ECommerceTemplateFields.PURCHASE_EVENT,
    ],
    keywords: [`Ecommerce`, `E-Commerce`, `Purchase`, `Retail`, `Cart analysis`, `Template`],
  },
  [TemplateType.SAAS_COMPANY_KPI]: {
    title: `B2B Account KPIs`,
    type: TemplateType.B2B,
    templateSelectDescription: `12 Reports and 4 Benchmarks based on the core metrics`,
    imageId: `saas-company-kpis`,
    demoBoardLink: `https://mixpanel.com/project/3409416/view/3914056/app/boards#id=8230017`,
    eventSelectStepDescription: `To use the SAAS Company KPI Template, select a group that represents an account along with a few other details.`,
    keywords: [],
    rhsDescription: `This template is all about your core company metrics and requires two events to get started.`,
    requiredFields: [
      SaaSCompanyKpiTemplateFields.DATA_GROUP_ID,
      SaaSCompanyKpiTemplateFields.MRR_PROPERTY_FIELD,
      SaaSCompanyKpiTemplateFields.PLAN_TYPE_PROPERTY_FIELD,
      SaaSCompanyKpiTemplateFields.SEGMENT_PROPERTY_FIELD,
    ],
  },
  [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: {
    title: `Lifecycle Cohort Analysis`,
    type: TemplateType.LIFECYCLE_COHORT_ANALYSIS,
    templateSelectDescription: `Ten reports to help you breakdown different users and their journey using your product.`,
    imageId: `lifecycle-cohort-analysis`,
    eventSelectStepDescription: `To use the Lifecycle Cohort Analysis Template, fill out your value moment so that we can uncover what causes change in your user base and can help you build more sustainable user growth.`,
    rhsDescription: `This template is about uncovering changes in your user base and building more sustainable user growth.`,
    demoBoardLink: `https://mixpanel.com/p/CEngFwLvPa5zvTSLvFTHNH`,
    moreInfo: {text: `Why these events?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT],
    keywords: [
      `LCA`,
      `Lifecycle`,
      `Cohort`,
      `Cohort analysis`,
      `User template`,
      `User journey`,
      `User segmentation`,
      `Value Moment`,
      `Value Moment Analysis`,
      `Resurrected User`,
      `Dormant user`,
      `Retained user`,
      `new user`,
      `Template`,
    ],
  },
  [TemplateType.B2B]: {
    title: `B2B Account KPIs`,
    type: TemplateType.B2B,
    templateSelectDescription: `Eight reports to help you understand your accounts ARR and logo mix.`,
    imageId: `kpi`,
    eventSelectStepDescription: `To use the B2B Template, select a group that represents an account along with a few other details.`,
    rhsDescription: `This template is all about your core company metrics and requires two events to get started.`,
    demoBoardLink: `https://mixpanel.com/p/QLBHa24vdYuK2MJLiNUA1S`,
    moreInfo: {text: `Why these fields?`, link: WHAT_TO_TRACK_URL},
    requiredFields: [
      B2BTemplateFields.DATA_GROUP_ID,
      B2BTemplateFields.ACTIVATION_EVENT,
      B2BTemplateFields.ARR_PROPERTY_FIELD,
    ],
    keywords: [
      `Company`,
      `KPI`,
      `KPIs`,
      `Key Performance Indicators`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `Core Performance Indicators`,
      `Core Metrics`,
      `DAU`,
      `MAU`,
      `WAU`,
      `Starter Template`,
      `Template`,
    ],
    featureRolloutName: FeatureRolloutName.WarehouseLaunchTemplates,
  },
};

type CreatorEditorInfo = {
  created?: string;
  creator_id: null | number;
  creator_name: null | string; // full name of creator
  creator_email: null | string;

  modified?: string;
  last_modified_by_id?: number;
  last_modified_by_email?: string;
  last_modified_by_name?: string;
};

// Bookmark format returned by the dashboards and bookmarks API
export type Bookmark<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = CreatorEditorInfo & {
  dashboard: DashboardOrigin;
  id: number;
  is_default: boolean;
  name: string;
  description?: string;
  original_type: string;
  params: BookmarkParamsType;
  project_id: number;
  icon?: string;
  workspace_id?: number;
  type: BookmarkType;
  error?: string;
  metadata?: any;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
};

// the only necessary fields needed to persist
export type DisplayBookmark<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = Pick<
  Bookmark<BookmarkParamsType>,
  `params` | `type` | `name` | `description`
>;

export interface DashboardCardChartOptions {
  type?: ChartType;
}

export type CardOnDashboard = DashboardCard | TextCard | MediaCard | NestedDashboardCard | BenchmarkCard;

export type SeenCards = Record<DashboardCardId, boolean>;

//TODO @shayan, since we added content_id, remove duplicate id types from cards after GA

// report or report link type card on the dashboard
export interface DashboardCard<BookmarkParamsType extends BookmarkParamsUnionType = TODO> {
  id: DashboardCardId;
  title?: string;
  description?: string;
  width: number;
  height: number;
  bookmark?: Bookmark<BookmarkParamsType>;
  bookmark_metadata?: any;
  chart_options?: DashboardCardChartOptions;
  linked_dashboard_card_id?: number;
  type?: Exclude<ContentType, ContentType.Spark>;
  source_bookmark_id?: number;
  content_id?: number;
}

export interface NestedDashboardCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type: ContentType.Dashboard;
  content_id: number;
  title: string;
  description: string;
  last_edited: string;
  thumbnail_layout: LayoutResponse;
  thumbnail_contents: ContentsResponse;
}

export interface TextCard {
  id: DashboardCardId;
  width: number;
  height: number;
  markdown: string;
  type?: Exclude<ContentType, ContentType.Spark>;
  text_card_id?: number;
  content_id?: number;
}
export interface BenchmarkCard<BookmarkParamsType extends BookmarkParamsUnionType = TODO> {
  id: DashboardCardId;
  width: number;
  height: number;
  benchmark_type: string;
  type: ContentType.Benchmark;
  industry: string;
  bookmark?: Bookmark<BookmarkParamsType>;
  bookmark_metadata?: any;
  content_id?: number;
}

export enum ContentType {
  ReportLink = `report-link`,
  Report = `report`,
  Text = `text`,
  Media = `media`,
  Dashboard = `dashboard`,
  // Special frontend only card. It is filtered out when saving.
  Spark = `spark`,
  Benchmark = `benchmark`,
}

export enum ContentActionType {
  Move = `move`,
  Delete = `delete`,
  Duplicate = `duplicate`, // for report/report-links, creates corresponding report
  Create = `create`,
  Update = `update`,
  Undelete = `undelete`,
  UnMove = `unmove`,
  CreateReportLink = `create-report-link`, // only for report/report-links
}

export interface ContentAction {
  actionType: ContentActionType;
  contentId?: number;
  contentType: ContentType;
  contentParams?: TODO;
}

/*
  Must match MediaCardType in dashboard/models.py
*/
export enum MediaCardType {
  Image = `image`,
  Video = `video`,
  EmbeddedImage = `embed_image`,
  EmbeddedVideo = `embed_video`,
}

export interface MediaCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type?: Exclude<ContentType, ContentType.Spark>;
  media_card_id?: number;
  media_type: MediaCardType;
  service: string;
  path: string;
  content_id?: number;
}

export enum DashboardType {
  UserCreated = `User Created`,
  TemplateGenerated = `Template Generated`,
  MyDashboard = `My Dashboard`,
  StagedTemplate = `Staged Template`,
}

export type Dashboard = CreatorEditorInfo & {
  id: number;
  title: string;
  description: null | string;
  bookmarks?: Bookmark[];
  card_order: DashboardCardId[];
  cards: CardOnDashboard[];
  time_filter: TimeFilter;
  filters: PersistedBoardFilter[];
  breakdowns?: Segmentation[];
  generation_type?: DashboardType;
  template_type?: TemplateType;

  // permissions
  can_view: boolean;
  can_update_basic: boolean;
  can_share: boolean;
  is_superadmin: boolean;
  allow_staff_override: boolean;
  can_pin_dashboards?: boolean;

  is_favorited?: boolean;
  pinned_date?: string;
  num_active_public_links?: number;
  project_id?: number;
  is_shared_with_project?: boolean;

  layout: LayoutResponse;
  contents: ContentsResponse;

  parent_dashboard_id?: number;
  ancestors?: Array<Pick<Dashboard, `id` | `title`>>;
  child_dashboards?: Dashboard[];
};

export type TimeFilter =
  | {
      displayText: Translatable;
      dateRange: DateRange;
    }
  | EmptyObj;

export enum DashboardQueryOrigin {
  Standard = `dashboard`,
  Digest = `dashboard_digest`,
  Public = `dashboard_public`,
  TvMode = `dashboard_tv_mode`,
  Home = `home_app`,
}

export enum BadLayoutRequestErrors {
  // the string values are defined in app_api
  InvalidLayout = `INVALID_LAYOUT`,
  TooManyMediaCardsOnDashboard = `TOO_MANY_DASH_MEDIA_CARDS`,
  TooManyMediaCardsInProject = `TOO_MANY_PROJECT_MEDIA_CARDS`,
}

export const BadLayoutRequestMessages = {
  [BadLayoutRequestErrors.InvalidLayout]: `Changes could not be saved due to a change conflict.`,
  [BadLayoutRequestErrors.TooManyMediaCardsOnDashboard]: `Changes could not be saved. This board has reached its media card limit.`,
  [BadLayoutRequestErrors.TooManyMediaCardsInProject]: `Changes could not be saved. This project has reached its media card limit.`,
};

export type LayoutCell = {
  id: string;
  content_id: string;
  content_type: ContentType;
  width: number;
};

export interface LayoutRowResponse {
  height: number;
  cells: LayoutCell[];
}

export type LayoutResponse =
  | EmptyObj
  | {
      version: string;
      order: string[];
      rows: {[id: string]: LayoutRowResponse};
    };

type BaseContent = {
  creator: string;
  creator_id: number;
  creator_name: string;
  creator_email: string;
  dashboard_id: number;
  id: number;
};

export type MediaContent = BaseContent & {
  media_type: MediaCardType;
  path: string;
  service: string;
};

export type TextContent = BaseContent & {
  markdown: string;
};

export type BenchmarkContent<BookmarkParamsType = TODO> = BaseContent & {
  benchmark_type: string;
  industry: string;
  params: BookmarkParamsType;
};

export type ReportContent = Bookmark;

export type ReportLinkContent = {
  id: number;
  bookmark: Bookmark;
};

export type NestedDashboardContent = Pick<
  Dashboard,
  `id` | `description` | `last_modified_by_email` | `last_modified_by_id` | `last_modified_by_name` | `creator_id`
> & {last_edited: string; thumbnail_layout: LayoutResponse; thumbnail_contents: ContentsResponse};

export type ContentsMapById<Content> = {
  [id: number]: Content;
};

export type ContentsResponse =
  | EmptyObj
  | {
      [ContentType.Media]: ContentsMapById<MediaContent>;
      [ContentType.Text]: ContentsMapById<TextContent>;
      [ContentType.Report]: ContentsMapById<ReportContent>;
      [ContentType.ReportLink]: ContentsMapById<ReportLinkContent>;
      [ContentType.Dashboard]: ContentsMapById<NestedDashboardContent>;
      [ContentType.Benchmark]: ContentsMapById<BenchmarkContent>;
    };

export type CardContent = ReportContent | ReportLinkContent | TextContent | MediaContent | BenchmarkContent;

export type BlueprintConfig = {
  blueprint_id: number;
  variables: {
    // event name -> blueprint name (blueprint name will be replaces)
    // example: "Purchase": "MP Blueprint Event 5",
    [eventName: string]: string;
  };
  eventNameMap: {
    // blueprint name -> real name
    // example: "MP Blueprint Event 5": "Purchase Completed",
    [blueprintName: string]: string;
  };
  cohorts: {
    [id: number]: Cohort;
  };
  scdPropEventMap: {
    [scdPropName: string]: string;
  };
};

export interface Trackable {
  toTrackingData(): Obj;
}

export type DataGroupNotSupportedReason = string | DataGroupNotSupportedReasonCode;

export enum DataGroupNotSupportedReasonCode {
  UserFilterOnDataGroup = `user_filter_on_data_group`,
  DataGroupIdMisMatch = `data_group_id_mismatch`,
}
